import { DragDropModule } from "@angular/cdk/drag-drop";
import { CdkScrollableModule, ScrollingModule } from "@angular/cdk/scrolling";
import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { NgbModule, NgbPopoverModule } from "@ng-bootstrap/ng-bootstrap";
import { NgSelectModule } from "@ng-select/ng-select";
import { AgGridModule } from "ag-grid-angular";
import { DayPilotModule } from "daypilot-pro-angular";
import { registerAllModules } from "handsontable/registry";
import { ProduktionsstatusPipe } from "~app/common/pipes/produktionsstatus.pipe";
import { DecimalPipe } from "./pipes/decimal.pipe";
import { FilterPipe } from "./pipes/filter.pipe";
import { FordelningPipe } from "./pipes/fordelning.pipe";
import { HighlightPipe } from "./pipes/highlight.pipe";
import { JoinValuesPipe } from "./pipes/join-values.pipe";
import { KilometerPipe } from "./pipes/kilometer.pipe";
import { LineBreakPipe } from "./pipes/line-break.pipe";
import { RaskNewlinesPipe } from "./pipes/rask-newlines.pipe";
import { RelativePipe } from "./pipes/relative.pipe";
import { SafePipe } from "./pipes/safe.pipe";
import { StringFormatPipe } from "./pipes/string-format.pipe";
import { StringLinkifyPipe } from "./pipes/string-linkify.pipe";
import { ActionButtonComponent } from "./components/action-button/action-button.component";
import { AddButtonComponent } from "./components/add-button/add-button.component";
import { ArbetsorderInfoIconComponent } from "./components/arbetsorder-info-icon/arbetsorder-info-icon.component";
import { BusySpinnerComponent } from "./components/busy/busy-spinner/busy-spinner.component";
import { BusyTreeComponent } from "./components/busy/busy-tree/busy-tree.component";
import { BusyComponent } from "./components/busy/busy.component";
import { ButtonStackComponent } from "./components/button-stack/button-stack.component";
import { ButtonComponent } from "./components/button/button.component";
import { CardComponent } from "./components/cards/card/card.component";
import { CardsComponent } from "./components/cards/cards.component";
import { ChartComponent } from "./components/chart/chart.component";
import { CheckboxItemComponent } from "./components/checkbox-list/checkbox-item/checkbox-item.component";
import { CheckboxListComponent } from "./components/checkbox-list/checkbox-list.component";
import { CheckboxComponent } from "./components/checkbox/checkbox.component";
import { CollapsableComponent } from "./components/collapsable/collapsable.component";
import { ColorPickerModalComponent } from "./components/color-picker/color-picker-modal/color-picker-modal.component";
import { ColorPickerComponent } from "./components/color-picker/color-picker.component";
import { CommandViewerComponent } from "./components/command-viewer/command-viewer.component";
import { BlockComponent } from "./components/container-block/block.component";
import { DataChangeLogComponent } from "./components/data-change-log/data-change-log.component";
import { DateDrillDownSelectorComponent } from "./components/date-drill-down/components/date-drill-down-selector/date-drill-down-selector.component";
import { DateDrillDownComponent } from "./components/date-drill-down/date-drill-down.component";
import { DatepickerIntervalComponent } from "./components/datepicker-interval/datepicker-interval.component";
import { DatepickerComponent } from "./components/datepicker/datepicker.component";
import { DirtyModalComponent } from "./components/dirty-modal/dirty-modal.component";
import { EnvironmentRibbonComponent } from "./components/environment-ribbon/environment-ribbon.component";
import { FieldsetToggleComponent } from "./components/fieldset-toggle/fieldset-toggle.component";
import { FileUploadComponent } from "./components/file-upload/file-upload.component";
import { FilterListComponent } from "./components/filter-list/filter-list.component";
import { FlapButtonComponent } from "./components/flap-button/flap-button.component";
import { EventhubStatusComponent } from "./components/footer/eventhub-status/eventhub-status.component";
import { FooterActivityDetailsComponent } from "./components/footer/footer-activity-details/footer-activity-details.component";
import { FooterActivityPopoverComponent } from "./components/footer/footer-activity-popover/footer-activity-popover.component";
import { FooterComponent } from "./components/footer/footer.component";
import { HeaderComponent } from "./components/header/header.component";
import { HelpiconComponent } from "./components/helpicon/helpicon.component";
import { HotDocComponent } from "./components/hot-doc/hot-doc.component";
import { IconButtonComponent } from "./components/icon-button/icon-button.component";
import { IconStackedComponent } from "./components/icon-stacked/icon-stacked.component";
import { IconComponent } from "./components/icon/icon.component";
import { InfoComponent } from "./components/info/info.component";
import { InlineToastComponent } from "./components/inline-toast/inline-toast.component";
import { InputNumberComponent } from "./components/input-number/input-number.component";
import { InputValidationIconComponent } from "./components/input-validation-icon/input-validation-icon.component";
import { InputComponent } from "./components/input/input.component";
import { LabelComponent } from "./components/label/label.component";
import { ListSelectedItemsComponent } from "./components/list-selected-items/list-selected-items.component";
import { DivListComponent } from "./components/list/div-list/div-list.component";
import { RowDragDirective } from "./components/list/div-list/row-drag.directive";
import { ListColumnCheckboxDirective } from "./components/list/list-column-checkbox.directive";
import { ListColumnDirective } from "./components/list/list-column.directive";
import { ListCustomRowDirective } from "./components/list/list-custom-row.directive";
import { ListComponent } from "./components/list/list.component";
import { VerksamhetshandelseLoggComponent } from "./components/loggning/verksamhetshandelseLogg.component";
import { MassupdateArbetsordrarComponent } from "./components/massupdate-arbetsordrar/massupdate-arbetsordrar.component";
import { MenuButtonComponent } from "./components/menu-button/menu-button.component";
import { MessagePanelItemComponent } from "./components/message-panel/message-panel-item/message-panel-item.component";
import { MessagePanelComponent } from "./components/message-panel/message-panel.component";
import { ConfirmModalComponent } from "./components/modal/common/confirm.component";
import { ErrorModalComponent } from "./components/modal/common/error.component";
import { InfoModalComponent } from "./components/modal/common/info.component";
import { PromptDialogComponent } from "./components/modal/common/prompt-dialog/prompt-dialog.component";
import { ModalComponent } from "./components/modal/modal.component";
import { MultiRangeSelectComponent } from "./components/multi-range-select/multi-range-select.component";
import { MultiSelectorComponent } from "./components/multi-selector/multi-selector.component";
import { PageComponent } from "./components/page/page.component";
import { PillToggleButtonComponent } from "./components/pill-toggle-button/pill-toggle-button.component";
import { PillboxComponent } from "./components/pillbox/pillbox.component";
import { QuickSearchComponent } from "./components/quick-search/quick-search.component";
import { ArbetsorderRendererComponent } from "./components/quick-search/search-result-renderers/arbetsorder-renderer/arbetsorder-renderer.component";
import { DefaultRendererComponent } from "./components/quick-search/search-result-renderers/default-renderer.component";
import { HandelssortimentRendererComponent } from "./components/quick-search/search-result-renderers/handelssortiment-renderer/handelssortiment-renderer.component";
import { KundLevTermOrderRendererComponent } from "./components/quick-search/search-result-renderers/kund-lev-term-order-renderer/kund-lev-term-order-renderer.component";
import { NoResultRendererComponent } from "./components/quick-search/search-result-renderers/no-result-renderer/no-result-renderer.component";
import { NoResultShowNavetRendererComponent } from "./components/quick-search/search-result-renderers/no-result-show-navet-renderer/no-result-show-navet-renderer.component";
import { ProduktRendererComponent } from "./components/quick-search/search-result-renderers/produkt-renderer/produkt-renderer.component";
import { ProduktinstruktionRendererComponent } from "./components/quick-search/search-result-renderers/produktinstruktion-renderer/produktinstruktion-renderer.component";
import { RendererResolverDirective } from "./components/quick-search/search-result-renderers/renderer-resolver.directive";
import { VirkeskopskontraktRendererComponent } from "./components/quick-search/search-result-renderers/virkeskopskontrakt-renderer/virkeskopskontrakt-renderer.component";
import { RadiobuttonComponent } from "./components/radiobuttongroup/radiobutton/radiobutton.component";
import { RadiobuttonGroupComponent } from "./components/radiobuttongroup/radiobuttongroup.component";
import { YesNoOptionsDirective } from "./components/radiobuttongroup/yes-no-options.directive";
import { RowComponent } from "./components/row/row.component";
import { SaveButtonComponent } from "./components/save-button/save-button.component";
import { SaveSearchFilterComponent } from "./components/save-search-filter/save-search-filter.component";
import { SavedSearchFilterTabsComponent } from "./components/saved-search-filter-tabs/saved-search-filter-tabs.component";
import { SearchComponent } from "./components/search/search.component";
import { SelectorLabelBindingDirective } from "./components/selector/selector-label-binding.directive";
import { SelectorValueBindingDirective } from "./components/selector/selector-value-binding.directive";
import { SelectorComponent } from "./components/selector/selector.component";
import { ShowHideComponent } from "./components/show-hide/show-hide.component";
import { SimpleSelectorComponent } from "./components/simple-selector/simple-selector.component";
import { SliderComponent } from "./components/slider/slider.component";
import { SpanComponent } from "./components/span/span.component";
import { StickySaveButtonComponent } from "./components/sticky-save-button/sticky-save-button.component";
import { SubmitButtonComponent } from "./components/submit-button/submit-button.component";
import { GridButtonComponent } from "./components/table/components/grid-button/grid-button.component";
import { GridCheckboxComponent } from "./components/table/components/grid-checkbox/grid-checkbox.component";
import { GridInputEditorComponent } from "./components/table/components/grid-input-editor/grid-input-editor.component";
import { GridInputNumberComponent } from "./components/table/components/grid-input-number/grid-input-number.component";
import { GridInputComponent } from "./components/table/components/grid-input/grid-input.component";
import { GridLinkComponent } from "./components/table/components/grid-link/grid-link.component";
import { GridMultiSelectEditorComponent } from "./components/table/components/grid-multi-select-editor/grid-multi-select-editor.component";
import { GridMultiSelectComponent } from "./components/table/components/grid-multi-select/grid-multi-select.component";
import { GridSelectorEditorComponent } from "./components/table/components/grid-selector-editor/grid-selector-editor.component";
import { GridSpanComponent } from "./components/table/components/grid-span/grid-span.component";
import { GridSrefComponent } from "./components/table/components/grid-sref/grid-sref.component";
import { GridTreeSeletorEditorComponent } from "./components/table/components/grid-tree-seletor-editor/grid-tree-seletor-editor.component";
import { FooterColumnComponent } from "./components/table/footer-column/footer-column.component";
import { GridHeaderComponent } from "./components/table/header/grid-header.component";
import { TableComponent } from "./components/table/table.component";
import { TabComponent } from "./components/tabs/tab/tab.component";
import { TabsComponent } from "./components/tabs/tabs.component";
import { TagComponent } from "./components/tag/tag.component";
import { TextExpanderComponent } from "./components/text-expander/text-expander.component";
import { TextareaComponent } from "./components/textarea/textarea.component";
import { TextmultilineComponent } from "./components/textmultiline/textmultiline.component";
import { ToastComponent } from "./components/toast/toast.component";
import { ToggleListItemComponent } from "./components/toggle-list/toggle-list-item/toggle-list-item.component";
import { ToggleListComponent } from "./components/toggle-list/toggle-list.component";
import { TogglePanelComponent } from "./components/toggle-panel/toggle-panel.component";
import { TreeSelectorComponent } from "./components/tree-selector/tree-selector.component";
import { VerticalCollapsableComponent } from "./components/vertical-collapsable/vertical-collapsable.component";
import { Viol3statusIndicatorComponent } from "./components/viol3status-indicator/viol3status-indicator.component";
import { VirkesorderstatusIndicatorComponent } from "./components/virkesorderstatus-indicator/virkesorderstatus-indicator.component";
import { BusyDirective } from "./directives/busy/busy.directive";
import { DisabledControlDirective } from "./directives/disabled-control/disabled-control.directive";
import { EnabledForEditRoleDirective } from "./directives/enabledForEditRole/enabled-for-edit-role.directive";
import { IfFeatureDirective } from "./directives/featuretoggle/if-feature.directive";
import { HasRoleDirective } from "./directives/has-role/has-role.directive";
import { HotkeyDirective } from "./directives/hotkey/hotkey.directive";
import { RaskPopoverDirective } from "./directives/popover/rask-popover.directive";
import { RequiredDirective } from "./directives/required/required.directive";
import { TableKeyboardNavigationDirective } from "./directives/table-keyboard-navigation/table-keyboard-navigation.directive";
import { WebinstallningDirective } from "./directives/webinstallning/webinstallning.directive";
import { ValidationMessageContainerComponent } from "./validation/validation-message-container/validation-message-container.component";
import { ValidationMessagePopoverComponent } from "./validation/validation-message-popover/validation-message-popover.component";
import { ValidationMessageRowComponent } from "./validation/validation-message-row/validation-message-row.component";
import { ValidationMessageComponent } from "./validation/validation-message/validation-message.component";
import { ValidationRuleMessageDirective } from "./validation/validation-rule-message.directive";
import { ValidationRuleDirective } from "./validation/validation-rule.directive";

registerAllModules();

@NgModule({
    exports: [
        EnvironmentRibbonComponent,
        QuickSearchComponent,
        RouterModule,
        HasRoleDirective,
        YesNoOptionsDirective,
        DisabledControlDirective,
        CommonModule,
        ValidationRuleMessageDirective,
        ModalComponent,
        MultiSelectorComponent,
        SearchComponent,
        TagComponent,
        ListComponent,
        ListColumnDirective,
        ListColumnCheckboxDirective,
        ListCustomRowDirective,
        SelectorComponent,
        DatepickerComponent,
        DirtyModalComponent,
        ErrorModalComponent,
        CollapsableComponent,
        FilterListComponent,
        RequiredDirective,
        IfFeatureDirective,
        BusyComponent,
        BusyDirective,
        NgbModule,
        NgbPopoverModule,
        FormsModule,
        ReactiveFormsModule,
        ConfirmModalComponent,
        InfoModalComponent,
        AddButtonComponent,
        DataChangeLogComponent,
        HighlightPipe,
        KilometerPipe,
        FilterPipe,
        StringFormatPipe,
        DecimalPipe,
        StringLinkifyPipe,
        SafePipe,
        RelativePipe,
        ShowHideComponent,
        HotkeyDirective,
        StickySaveButtonComponent,
        ValidationMessageComponent,
        InputComponent,
        InputNumberComponent,
        CommandViewerComponent,
        BlockComponent,
        PageComponent,
        FooterComponent,
        IconComponent,
        IconStackedComponent,
        ButtonComponent,
        CheckboxComponent,
        RadiobuttonComponent,
        RadiobuttonGroupComponent,
        TreeSelectorComponent,
        TextExpanderComponent,
        RaskNewlinesPipe,
        TabsComponent,
        HeaderComponent,
        TabComponent,
        ActionButtonComponent,
        IconButtonComponent,
        InfoComponent,
        TextareaComponent,
        TableComponent,
        FooterColumnComponent,
        GridButtonComponent,
        AgGridModule,
        TogglePanelComponent,
        SliderComponent,
        HotDocComponent,
        DateDrillDownComponent,
        DateDrillDownSelectorComponent,
        WebinstallningDirective,
        EnabledForEditRoleDirective,
        DatepickerIntervalComponent,
        CheckboxListComponent,
        ColorPickerComponent,
        ToggleListItemComponent,
        ToggleListComponent,
        LabelComponent,
        FieldsetToggleComponent,
        SpanComponent,
        SubmitButtonComponent,
        MultiRangeSelectComponent,
        TextmultilineComponent,
        FordelningPipe,
        BusySpinnerComponent,
        BusyTreeComponent,
        MessagePanelComponent,
        ButtonStackComponent,
        ValidationMessageRowComponent,
        ValidationRuleDirective,
        ValidationMessageContainerComponent,
        VirkesorderstatusIndicatorComponent,
        ChartComponent,
        SaveButtonComponent,
        VerticalCollapsableComponent,
        VerksamhetshandelseLoggComponent,
        DragDropModule,
        CardsComponent,
        CardComponent,
        MenuButtonComponent,
        ArbetsorderInfoIconComponent,
        MassupdateArbetsordrarComponent,
        DivListComponent,
        Viol3statusIndicatorComponent,
        SimpleSelectorComponent,
        PromptDialogComponent,
        SaveSearchFilterComponent,
        SavedSearchFilterTabsComponent,
        InlineToastComponent,
        ListSelectedItemsComponent,
        InputValidationIconComponent,
        SelectorValueBindingDirective,
        SelectorLabelBindingDirective,
        TableKeyboardNavigationDirective,
        RowComponent,
        PillboxComponent,
        ToastComponent,
        PillToggleButtonComponent,
        RaskPopoverDirective,
        ProduktionsstatusPipe,
        FileUploadComponent,
        HelpiconComponent,
        JoinValuesPipe
    ],
    imports: [
        CommonModule,
        DayPilotModule,
        FormsModule,
        ReactiveFormsModule,
        NgbModule,
        NgbPopoverModule,
        RouterModule,
        AgGridModule,
        NgSelectModule,
        DragDropModule,
        CdkScrollableModule,
        ScrollingModule,
        ProduktionsstatusPipe,
        FileUploadComponent,
        JoinValuesPipe,
        DisabledControlDirective,
        ValidationRuleMessageDirective,
        ModalComponent,
        SelectorComponent,
        MultiSelectorComponent,
        TagComponent,
        ListComponent,
        ListColumnDirective,
        ListColumnCheckboxDirective,
        ListCustomRowDirective,
        DatepickerComponent,
        DirtyModalComponent,
        ErrorModalComponent,
        CollapsableComponent,
        FilterListComponent,
        ConfirmModalComponent,
        InfoModalComponent,
        RequiredDirective,
        IfFeatureDirective,
        BusyComponent,
        BusyDirective,
        VerksamhetshandelseLoggComponent,
        TextExpanderComponent,
        RaskNewlinesPipe,
        AddButtonComponent,
        DataChangeLogComponent,
        HighlightPipe,
        KilometerPipe,
        FilterPipe,
        StringFormatPipe,
        DecimalPipe,
        LineBreakPipe,
        StringLinkifyPipe,
        SafePipe,
        ShowHideComponent,
        HotkeyDirective,
        ToastComponent,
        StickySaveButtonComponent,
        ValidationMessageComponent,
        InputComponent,
        CommandViewerComponent,
        InputNumberComponent,
        ButtonComponent,
        CheckboxComponent,
        RadiobuttonComponent,
        RadiobuttonGroupComponent,
        IconComponent,
        InfoComponent,
        BlockComponent,
        PageComponent,
        FooterComponent,
        CommandViewerComponent,
        TreeSelectorComponent,
        TabsComponent,
        TabComponent,
        ActionButtonComponent,
        HeaderComponent,
        TextareaComponent,
        TableComponent,
        GridCheckboxComponent,
        GridInputComponent,
        GridLinkComponent,
        GridSrefComponent,
        GridSpanComponent,
        GridInputNumberComponent,
        GridButtonComponent,
        FooterColumnComponent,
        TogglePanelComponent,
        SliderComponent,
        HotDocComponent,
        WebinstallningDirective,
        EnabledForEditRoleDirective,
        DateDrillDownComponent,
        DateDrillDownSelectorComponent,
        SearchComponent,
        DatepickerIntervalComponent,
        CheckboxListComponent,
        CheckboxItemComponent,
        ColorPickerComponent,
        ColorPickerModalComponent,
        ToggleListItemComponent,
        ToggleListComponent,
        LabelComponent,
        FieldsetToggleComponent,
        SpanComponent,
        SubmitButtonComponent,
        MultiRangeSelectComponent,
        TextmultilineComponent,
        FordelningPipe,
        IconButtonComponent,
        BusySpinnerComponent,
        ButtonStackComponent,
        MessagePanelComponent,
        MessagePanelItemComponent,
        ValidationMessageRowComponent,
        ValidationRuleDirective,
        ValidationMessageContainerComponent,
        VirkesorderstatusIndicatorComponent,
        ChartComponent,
        SaveButtonComponent,
        ValidationMessagePopoverComponent,
        GridHeaderComponent,
        VerticalCollapsableComponent,
        FlapButtonComponent,
        CardsComponent,
        CardComponent,
        QuickSearchComponent,
        DefaultRendererComponent,
        RendererResolverDirective,
        ArbetsorderRendererComponent,
        KundLevTermOrderRendererComponent,
        HandelssortimentRendererComponent,
        ProduktinstruktionRendererComponent,
        ProduktRendererComponent,
        VirkeskopskontraktRendererComponent,
        MenuButtonComponent,
        IconStackedComponent,
        RowDragDirective,
        ArbetsorderInfoIconComponent,
        MassupdateArbetsordrarComponent,
        DivListComponent,
        GridMultiSelectComponent,
        GridTreeSeletorEditorComponent,
        GridSelectorEditorComponent,
        GridInputEditorComponent,
        GridMultiSelectEditorComponent,
        Viol3statusIndicatorComponent,
        SimpleSelectorComponent,
        NoResultRendererComponent,
        NoResultShowNavetRendererComponent,
        RelativePipe,
        PromptDialogComponent,
        YesNoOptionsDirective,
        SaveSearchFilterComponent,
        SavedSearchFilterTabsComponent,
        InlineToastComponent,
        ListSelectedItemsComponent,
        InputValidationIconComponent,
        SelectorValueBindingDirective,
        SelectorLabelBindingDirective,
        FooterActivityPopoverComponent,
        FooterActivityDetailsComponent,
        RowComponent,
        PillboxComponent,
        TableKeyboardNavigationDirective,
        BusyTreeComponent,
        HasRoleDirective,
        EnvironmentRibbonComponent,
        PillToggleButtonComponent,
        RaskPopoverDirective,
        HelpiconComponent,
        EventhubStatusComponent

    ],
    providers: [ProduktionsstatusPipe]
})
export class RaskCommonModule {
}
